import { Component, computed, inject, signal } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';

import { EmailNode } from '@modules/campaigns/Domain/Nodes/EmailNode';
import { EmailConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';

@Component({
  selector: 'app-email-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    DropdownModule,
    CalendarModule,
    RadioButtonModule,
    DatePipe,
  ],
  templateUrl: './email-inspector.component.html',
  styleUrls: ['./email-inspector.component.scss'],
})
export class EmailInspectorComponent extends InspectorBase<EmailNode> {
  today = new Date();
  expirationDays = signal<number | null>(180);
  expirationAt = signal<Date | null>(null);
  expirationMode = signal<'days' | 'date'>('days');
  sendToAllContacts = signal<boolean>(false);
  selectedTemplateId = signal<string | null>(null);
  templates = signal<CopyModel[]>([]);

  selectedTemplate = computed(() => {
    const templates = this.templates();
    const selectedId = this.selectedTemplateId();
    if (!templates.length || !selectedId) return null;

    return templates.find((t) => t.id === selectedId) ?? null;
  });

  previewSubject = computed(() => {
    const template = this.selectedTemplate();
    if (!template) return '';

    const subjectBlock = template.blocks?.find(
      (b) => b.type === 'subject' || b.alias === 'subject',
    );
    if (subjectBlock) return subjectBlock.translations?.[0]?.text ?? '';

    return '';
  });

  previewMessage = computed(() => {
    const template = this.selectedTemplate();
    if (!template) return '';

    const htmlBlock = template.blocks?.find(
      (b) =>
        b.type === 'emailHtml' ||
        b.type === 'html' ||
        b.alias === 'emailHtml' ||
        b.type === 'text' ||
        b.alias === 'text',
    );
    if (htmlBlock) return htmlBlock.translations?.[0]?.text ?? '';

    const textBlock = template.blocks?.find(
      (b) => b.type === 'emailText' || b.type === 'text' || b.alias === 'emailText',
    );
    if (textBlock) return textBlock.translations?.[0]?.text ?? '';

    return '';
  });

  selectedTemplateName = computed(() => {
    return this.selectedTemplate()?.name ?? '';
  });

  editTemplateUrl = computed(() => {
    const templateId = this.selectedTemplateId();
    if (!templateId) return null;

    const tenantId = this.route.snapshot.queryParams['tenant'];
    return `/copy/edit/${templateId}?tenant=${tenantId}`;
  });

  expirationModeOptions = [
    { label: '', value: 'days', icon: 'pi pi-calendar' },
    { label: '', value: 'date', icon: 'pi pi-clock' },
  ];

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
    private copiesService: CopiesService,
    private route: ActivatedRoute,
  ) {
    super();
    this.expirationModeOptions = [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.EMAIL.CONFIG.EXPIRATION_MODE_DAYS'),
        value: 'days',
        icon: 'pi pi-clock',
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.EMAIL.CONFIG.EXPIRATION_MODE_DATE'),
        value: 'date',
        icon: 'pi pi-calendar',
      },
    ];
  }

  override get nodeValue(): EmailNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.SendEmail ? (current as EmailNode) : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.EMAIL.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.EMAIL.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  loadTemplates() {
    this.copiesService.searchCopies().subscribe((copies: CopyModel[]) => {
      console.log(copies);
      const emailTemplates = copies.filter((c) => c.type === 'email');
      this.templates.set(emailTemplates);
    });
  }

  override setNode(nodeSignal: any) {
    super.setNode(nodeSignal);
    this.loadTemplates();
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const mode = this.expirationMode();
    const expAt = mode === 'date' ? this.expirationAt() : null;
    const expDays = expAt ? 0 : this.expirationDays() ?? 0;
    const selectedId = this.selectedTemplateId();

    const updatedConfig: EmailConfig = {
      ...(node.config as EmailConfig),
      copyIds: selectedId ? [selectedId] : [],
      copyId: selectedId || null,
      expirationDays: expDays,
      expirationAt: expAt,
      sendToAllContacts: this.sendToAllContacts(),
    } as EmailConfig;

    const updated = node.cloneWithConfig(updatedConfig) as EmailNode;
    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  onTemplateChange(id: string) {
    this.selectedTemplateId.set(id);
    this.scheduleApplyChanges();
  }

  onExpirationModeChange(mode: 'days' | 'date') {
    this.expirationMode.set(mode);
    this.expirationDays.set(180);
    this.scheduleApplyChanges();
  }

  onExpirationDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 0;
    if (value < 0) value = 0;
    if (value > 365) value = 365;

    this.expirationDays.set(value);
    this.scheduleApplyChanges();
  }

  onExpirationAtChange(value: Date | null) {
    this.expirationAt.set(value);
    this.scheduleApplyChanges();
  }

  onSendToAllContactsChange(value: boolean) {
    this.sendToAllContacts.set(!!value);
    this.scheduleApplyChanges();
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as EmailConfig;

    this.selectedTemplateId.set(cfg.copyIds?.[0] ?? null);
    this.expirationDays.set(cfg.expirationDays ?? 180);
    this.expirationAt.set(cfg.expirationAt ? new Date(cfg.expirationAt) : null);
    this.expirationMode.set(cfg.expirationAt ? 'date' : 'days');
    this.sendToAllContacts.set(cfg.sendToAllContacts ?? false);

    this.errors.set([]);
  }
}
